import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../context/AppContext";
import PageTransition from "../components/PageTransition";
import config from "../config"
interface Chat {
  id: string;
  title: string;
  status: "active" | "completed";
}

const ChatList: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { loading, setLoading } = useApp();
  const [chats, setChats] = useState<Chat[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [formData, setFormData] = useState({
    title: "",
  });

  useEffect(() => {
    fetchChats();
  }, [token]);

  const fetchChats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(config.baseBackURL + "/api/chats", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChats(response.data);
    } catch (error) {
      console.error("Error fetching chats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (chat?: Chat) => {
    if (chat) {
      setSelectedChat(chat);
      setFormData({
        title: chat.title,
      });
    } else {
      setSelectedChat(null);
      setFormData({
        title: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedChat(null);
    setFormData({
      title: "",
    });
  };

  const handleSubmit = async () => {
    try {
      if (selectedChat) {
        await axios.put(
          config.baseBackURL + `/api/chats/${selectedChat.id}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        fetchChats();
        handleCloseDialog();
      } else {
        const response = await axios.post(
          config.baseBackURL + "/api/chats",
          { title: formData.title || "Новый чат" },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        handleCloseDialog();
        navigate(`/chat/${response.data.id}`);
      }
    } catch (error) {
      console.error("Error saving chat:", error);
    }
  };

  const handleDelete = async (chatId: string) => {
    if (window.confirm("Вы уверены, что хотите удалить этот чат?")) {
      try {
        await axios.delete(config.baseBackURL + `/api/chats/${chatId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchChats();
      } catch (error) {
        console.error("Error deleting chat:", error);
      }
    }
  };

  const handleCreateNewChat = async () => {
    try {
      const response = await axios.post(
        config.baseBackURL + "/api/chats",
        { title: "Новый чат" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/chat/${response.data.id}`);
    } catch (error) {
      console.error("Error creating chat:", error);
    }
  };

  return (
    <PageTransition>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h4">Мои чаты</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateNewChat}
          >
            Новый чат
          </Button>
        </Box>

        <Paper>
          <List>
            {chats.map((chat) => (
              <ListItem
                key={chat.id}
                button
                onClick={() => navigate(`/chat/${chat.id}`)}
              >
                <ListItemText
                  primary={chat.title || "Новый чат"}
                  secondary={`Статус: ${
                    chat.status === "completed" ? "Завершен" : "Активный"
                  }`}
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Редактировать название">
                    <IconButton
                      edge="end"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(chat);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Удалить чат">
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(chat.id);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>

        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>Редактировать название чата</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Название чата"
              fullWidth
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button onClick={handleSubmit} color="primary">
              Сохранить
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageTransition>
  );
};

export default ChatList;
